/**
 * One attendance status, rendered the only way this product renders one.
 *
 * A wrapper over `Badge` (Task 42) rather than a second badge component, and the
 * wrapper exists for one reason: it takes an `AttendanceBadgeStatus` and **nothing
 * else**. There is no prop for the icon, no prop for the severity and no prop for
 * the label, so a caller cannot render a status with a colour and no words, or with
 * the wrong glyph, or with a label it invented. `STATUS_PRESENTATION` is the single
 * table and this is the single door to it.
 *
 * `DESIGN.md` §4 rule 4 is what that buys: "on-time / late / early / missing / leave
 * / incomplete are icon + text, not six colours." Rule 2 adds that the icons must
 * differ in **silhouette**, and `badges.test.tsx` asserts exactly that with every
 * colour-bearing attribute stripped out of the tree first.
 */

import type { ReactNode } from 'react';

import { useT } from '../../i18n';
import { STATUS_PRESENTATION } from '../../attendance/status';
import type { AttendanceBadgeStatus } from '../../api/types';
import { Badge } from '../../states/Badge';

export interface StatusBadgeProps {
  readonly status: AttendanceBadgeStatus;
  readonly testId?: string;
}

export function StatusBadge({ status, testId }: StatusBadgeProps): ReactNode {
  const t = useT();
  const presentation = STATUS_PRESENTATION[status];
  return (
    <Badge
      severity={presentation.severity}
      icon={presentation.icon}
      label={t(presentation.labelKey)}
      title={t(presentation.helpKey)}
      {...(presentation.tone === undefined ? {} : { tone: presentation.tone })}
      {...(testId === undefined ? {} : { testId })}
    />
  );
}
